import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Alert,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  MoreVert as MoreIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Layers as DefaultConfigIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import { Team, TeamFormData } from '../types';
import { useAuth } from '../contexts/AuthContext';
import teamsApi from '../services/teamsApi';

const teamSchema = yup.object({
  name: yup.string().required('Team name is required').min(1, 'Team name must be at least 1 character'),
  description: yup.string().optional(),
  members: yup.array().of(yup.string().required()).required().min(1, 'At least one member is required'),
});

const TeamsPage: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<TeamFormData>({
    resolver: yupResolver(teamSchema),
    defaultValues: {
      name: '',
      description: '',
      members: []
    }
  });

  const watchedMembers = watch('members');

  useEffect(() => {
    // Check if user has admin+ permissions
    if (!user || !['admin', 'superuser'].includes(user.role)) {
      return;
    }
    
    loadTeams();
  }, [user]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const teamsData = await teamsApi.getTeams();
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to load teams:', error);
      enqueueSnackbar('Failed to load teams', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = () => {
    setEditingTeam(null);
    reset({ name: '', description: '', members: [] });
    setDialogOpen(true);
  };

  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    reset({
      name: team.name,
      description: team.description || '',
      members: team.members
    });
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteTeam = async (team: Team) => {
    if (window.confirm(`Are you sure you want to delete the "${team.name}" team?`)) {
      try {
        await teamsApi.deleteTeam(team.id);
        await loadTeams();
        enqueueSnackbar('Team deleted successfully', { variant: 'success' });
      } catch (error) {
        console.error('Failed to delete team:', error);
        enqueueSnackbar('Failed to delete team', { variant: 'error' });
      }
    }
    handleMenuClose();
  };

  const onSubmit = async (data: TeamFormData) => {
    try {
      if (editingTeam) {
        await teamsApi.updateTeam(editingTeam.id, data);
        enqueueSnackbar('Team updated successfully', { variant: 'success' });
      } else {
        await teamsApi.createTeam(data);
        enqueueSnackbar('Team created successfully', { variant: 'success' });
      }
      
      setDialogOpen(false);
      await loadTeams();
    } catch (error) {
      console.error('Failed to save team:', error);
      enqueueSnackbar('Failed to save team', { variant: 'error' });
    }
  };

  const handleAddMember = () => {
    if (newMemberEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMemberEmail)) {
      const currentMembers = watchedMembers || [];
      if (!currentMembers.includes(newMemberEmail.trim())) {
        setValue('members', [...currentMembers, newMemberEmail.trim()]);
        setNewMemberEmail('');
      }
    }
  };

  const handleRemoveMember = (emailToRemove: string) => {
    const currentMembers = watchedMembers || [];
    setValue('members', currentMembers.filter(email => email !== emailToRemove));
  };

  const handleMemberKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddMember();
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, team: Team) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTeam(team);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTeam(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTeam(null);
    reset();
  };

  // Check permissions
  if (!user || !['admin', 'superuser'].includes(user.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access team management.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            sx={{ 
              backgroundColor: 'action.hover',
              '&:hover': {
                backgroundColor: 'action.selected',
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" gutterBottom>
              Team Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage teams for easy group sharing
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Default Config - only show for superuser */}
          {user?.role === 'superuser' && (
            <Button
              startIcon={<DefaultConfigIcon />}
              onClick={() => navigate('/default-config')}
              variant="outlined"
            >
              Default Config
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTeam}
            sx={{ minWidth: 140 }}
          >
            Create Team
          </Button>
        </Box>
      </Box>

      {/* Teams Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map((n) => (
            <Grid item xs={12} md={6} lg={4} key={n}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : teams.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            borderRadius: 2,
            backgroundColor: 'action.hover',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No teams created yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Create your first team to start organizing your group sharing
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTeam}
          >
            Create Your First Team
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {teams.map((team) => (
            <Grid item xs={12} md={6} lg={4} key={team.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                      {team.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, team)}
                      sx={{ ml: 1 }}
                    >
                      <MoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {team.description && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mb: 2, minHeight: 40 }}
                    >
                      {team.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PeopleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">
                      {team.members.length} member{team.members.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {team.members.slice(0, 3).map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                    {team.members.length > 3 && (
                      <Chip
                        label={`+${team.members.length - 3} more`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </CardContent>
                
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditTeam(team)}
                  >
                    Edit
                  </Button>
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                    Created by {team.creator_email}
                  </Typography>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => selectedTeam && handleEditTeam(selectedTeam)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Team</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={() => selectedTeam && handleDeleteTeam(selectedTeam)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Team</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Team Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Create New Team'}
        </DialogTitle>
        
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Team Name */}
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Team Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>

              {/* Team Description */}
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description (Optional)"
                      multiline
                      rows={2}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>

              {/* Team Members */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Team Members
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Enter email address"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    onKeyPress={handleMemberKeyPress}
                    sx={{ flex: 1 }}
                    error={!!(newMemberEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMemberEmail))}
                    helperText={newMemberEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMemberEmail) ? 'Please enter a valid email' : ''}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={handleAddMember}
                    disabled={!newMemberEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMemberEmail)}
                  >
                    Add
                  </Button>
                </Box>

                {/* Member List */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(watchedMembers || []).map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveMember(email)}
                      deleteIcon={<PersonRemoveIcon />}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>

                {errors.members && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors.members.message}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting}
            sx={{ minWidth: 120 }}
          >
            {isSubmitting ? 'Saving...' : (editingTeam ? 'Update Team' : 'Create Team')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamsPage;